import Vue from 'vue';
import { database } from '@/plugins/firebase';
import InstructorTable from '@/classes/InstructorTable';
import moment from 'moment';
import store from "@/store";
import { seasonCollection } from "@/store/seasonYear";
import { getDocumentVersion } from '@/utils/versionControl';
import { callFn } from '@/utils/functionsClient';
export const InstructorTableModule = {
    namespaced: true,
    state: {
        tables: [],
        copyPasteCell: undefined,
        selection: { date: '', cells: [] },
        versions: {},
        bases: {}
    },
    mutations: {
        store(state, table) {
            const x = state.tables.find(x => x.date.format('YYYY-MM-DD') === table.data.date);
            if (x) {
                if (x.changedAt === table.data.changedAt) {
                    return;
                }
                state.tables.splice(state.tables.indexOf(x), 1, InstructorTable.loadFromObject(table.data, table.id));
                store.dispatch('instructorTable/storeCache', table.date);
                return;
            }
            state.tables.push(InstructorTable.loadFromObject(table.data, table.id));
        },
        update(state, table) {
            state.tables.find((oldTable, index) => {
                if (oldTable.date.format('YYYY-MM-DD') === table.data.date) {
                    Vue.set(state.tables, index, InstructorTable.loadFromObject(table.data, table.id));
                }
            });
        },
        clear(state) {
            state.tables.splice(0, state.tables.length);
        },
        copy(state, cell) {
            state.copyPasteCell = cell;
        },
        clearCopyPaste(state) {
            state.copyPasteCell = undefined;
        },
        /** A lesson from another day replaces the selection; one receipt is one day. */
        toggleSelection(state, target) {
            const cell = { rowKey: target.rowKey, hour: target.hour };
            if (state.selection.date !== target.date) {
                state.selection = { date: target.date, cells: [cell] };
                return;
            }
            const index = state.selection.cells.findIndex(x => x.rowKey === cell.rowKey && x.hour === cell.hour);
            if (index === -1) {
                state.selection.cells.push(cell);
                return;
            }
            state.selection.cells.splice(index, 1);
        },
        clearSelection(state) {
            state.selection = { date: '', cells: [] };
        },
        /**
         * Set version for a specific date.
         * Feature: 003-bug-fixes (FR-004)
         */
        setVersion(state, { date, version }) {
            Vue.set(state.versions, date, version);
        },
        /**
         * Version and merge base from one snapshot: the server merges the sent
         * table against the base, so the two must come from the same document.
         */
        setSnapshot(state, { date, data }) {
            Vue.set(state.versions, date, getDocumentVersion(data));
            Vue.set(state.bases, date, data);
        },
        /**
         * Clear version for a specific date (when table is unloaded).
         * Feature: 003-bug-fixes (FR-004)
         */
        clearVersion(state, date) {
            Vue.delete(state.versions, date);
            Vue.delete(state.bases, date);
        }
    },
    actions: {
        //ToDo Opravit typ u data parametru. měl by stačit pouze datum
        async refreshDate(context, data) {
            const dateHours = context.getters['get'](data.date);
            if (dateHours !== undefined) {
                context.commit('update', {
                    id: dateHours.id,
                    data: {
                        ...dateHours.toSaveObject(),
                        kidSchools: context.rootGetters['kidSchool/find'](data.date)
                    },
                });
            }
        },
        /** The kid school list arrives after the day may already be rendered without it. */
        async refreshKidSchools(context) {
            for (const table of [...context.state.tables]) {
                await Promise.all(context.rootGetters['kidSchool/find'](table.date).map((kidSchool) => context.dispatch('kidSchool/getRoster', kidSchool.id, { root: true }))).catch((e) => console.error('Failed to load kid school rosters:', e));
                await context.dispatch('refreshDate', { date: table.date });
            }
        },
        async loadDates(context, dates) {
            const promises = [];
            for (const date of dates) {
                if (context.getters['get'](date)) {
                    return;
                }
                const promise = database.collection(seasonCollection('works'))
                    .doc(date.format('YYYY-MM-DD'))
                    .get();
                promises.push(promise);
                // async run
                promises.push((async () => {
                    const doc = await promise;
                    await Promise.all(context.rootGetters['kidSchool/find'](date).map((kidSchool) => context.dispatch('kidSchool/getRoster', kidSchool.id, { root: true }))).catch((e) => console.error('Failed to load kid school rosters:', e));
                    const docData = doc.data();
                    // A soft-deleted day is a day nobody opened yet.
                    if (docData && !docData.deletedAt) {
                        // Capture version and merge base for optimistic locking (FR-004)
                        context.commit('setSnapshot', { date: date.format('YYYY-MM-DD'), data: docData });
                        context.commit('store', {
                            id: doc.id,
                            data: {
                                ...docData,
                                kidSchools: context.rootGetters['kidSchool/find'](docData.date)
                            },
                            date,
                        });
                    }
                    else {
                        // New document starts at version 0 (FR-004)
                        context.commit('setVersion', { date: date.format('YYYY-MM-DD'), version: 0 });
                        context.commit('store', {
                            data: {
                                date: date.format('YYYY-MM-DD'),
                                concluded: false,
                                kidSchools: context.rootGetters['kidSchool/find'](date),
                            },
                            date,
                        });
                    }
                    context.dispatch('storeCache', date);
                })());
            }
            return Promise.all(promises);
        },
        async store(context, table) {
            await callFn('saveWorkTable', {
                date: table.date.format('YYYY-MM-DD'),
                expectedVersion: 0,
                table: table.toSaveObject(),
            });
        },
        async update(context, table) {
            const dateKey = table.date.format('YYYY-MM-DD');
            const expectedVersion = context.state.versions[dateKey] ?? 0;
            const base = context.state.bases[dateKey];
            if (table.id === undefined && table.rows.length === 0) {
                context.commit('update', { data: {
                        ...table.toSaveObject(),
                        kidSchools: context.rootGetters['kidSchool/find'](table.date)
                    } });
                return;
            }
            await callFn('saveWorkTable', {
                date: dateKey,
                expectedVersion,
                table: table.toSaveObject(),
                // Without it the server can only refuse a stale save, not merge it.
                ...(base ? { base } : {}),
            });
        },
        /** Replaces local (optimistic) state with the stored document after a failed save. */
        async reload(context, id) {
            const doc = await database.collection(seasonCollection('works')).doc(id).get();
            const docData = doc.data();
            if (!docData) {
                return;
            }
            context.commit('setSnapshot', { date: docData.date, data: docData });
            context.commit('update', { id: doc.id, data: {
                    ...docData,
                    kidSchools: context.rootGetters['kidSchool/find'](docData.date)
                } });
        },
        async destroy(context, table) {
            const dateKey = table.date.format('YYYY-MM-DD');
            const expectedVersion = context.state.versions[dateKey] ?? 0;
            await callFn('saveWorkTable', {
                date: dateKey,
                expectedVersion,
                table: { ...table.toSaveObject(), rows: [] },
            });
        },
        async listen(context, data) {
            //context.commit('clear');
            //await context.dispatch('loadCache', date);
            const { date, callback } = data;
            const dateKey = date.format('YYYY-MM-DD');
            // The day whose work this listener has seen, so emptiness that was
            // never there does not overwrite work waiting to be saved.
            let seen = false;
            return database.collection(seasonCollection('works'))
                .doc(dateKey)
                .onSnapshot(doc => {
                const docData = doc.data();
                // A soft-deleted day is gone, document or no document.
                const alive = docData !== undefined && !docData.deletedAt;
                if (!alive && !seen) {
                    return;
                }
                seen = alive;
                if (alive) {
                    // Update version and merge base from real-time changes (FR-004)
                    context.commit('setSnapshot', { date: dateKey, data: docData });
                    context.commit('update', { id: doc.id, data: {
                            ...docData,
                            kidSchools: context.rootGetters['kidSchool/find'](date)
                        } });
                }
                else {
                    context.commit('clearVersion', dateKey);
                    context.commit('update', { data: {
                            date: dateKey,
                            concluded: false,
                            kidSchools: context.rootGetters['kidSchool/find'](date)
                        } });
                }
                context.dispatch('storeCache', date);
                if (callback) {
                    callback(alive ? 'modified' : 'removed');
                }
            });
        },
        async storeCache(context, date) {
            localStorage.removeItem(seasonCollection('works'));
            const tables = {};
            const cacheFrom = moment().subtract('3', 'weeks');
            const cacheTo = moment().add('4', 'weeks');
            for (const table of context.state.tables) {
                if (table.date.isBetween(cacheFrom, cacheTo, 'days')) {
                    tables[table.id] = table.toSaveObject();
                }
            }
            try {
                localStorage.setItem(seasonCollection('works') + date.format('YYYY-MM-DD'), JSON.stringify(tables));
            }
            catch (e) { }
        },
        async loadCache(context, date) {
            const cache = localStorage.getItem(seasonCollection('works') + date.format('YYYY-MM-DD'));
            if (!cache) {
                return;
            }
            const tables = JSON.parse(cache);
            for (const table of Object.values(tables)) {
                context.commit('store', {
                    id: table.id,
                    data: {
                        ...table,
                        kidSchools: context.rootGetters['kidSchool/find'](moment(table.date)),
                    },
                    date,
                });
            }
        },
        copyCell(context, cell) {
            context.commit('copy', cell);
        },
        clearCell(context, cell) {
            context.commit('clearCopyPaste');
        }
    },
    getters: {
        all(state) {
            return state.tables;
        },
        async fetchAll(state) {
            const docs = await database.collection(seasonCollection('works')).get();
            const ret = [];
            docs.forEach(doc => {
                ret.push({
                    id: doc.id,
                    data: doc.data(),
                });
            });
            return ret;
        },
        get(state) {
            return (date) => state.tables.find(x => x.date.format('YYYY-MM-DD') === date.format('YYYY-MM-DD'));
        },
        getCopyPasteCell(state) {
            return state.copyPasteCell;
        },
        selectedLessons(state) {
            return (date) => state.selection.date === date ? state.selection.cells : [];
        },
        isSelected(state) {
            return (date, rowKey, hour) => state.selection.date === date
                && state.selection.cells.some(x => x.rowKey === rowKey && x.hour === hour);
        },
        /**
         * Get version for a specific date.
         * Feature: 003-bug-fixes (FR-004)
         */
        getVersion(state) {
            return (date) => state.versions[date] ?? 0;
        }
    },
};
